// src/pages/admin/ClientsOverview.js
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../../lib/firebase";

function getMillis(ts) {
  if (!ts) return 0;
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.getTime();
}

function formatAddress(c) {
  const parts = [
    c.addressLine1,
    c.addressLine2,
    c.city,
    c.region,
    c.postalCode,
    c.country,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "-";
}

const SORTS = [
  { key: "created_desc", label: "Newest clients" },
  { key: "created_asc", label: "Oldest clients" },
  { key: "updated_desc", label: "Last updated" },
  { key: "name_asc", label: "Name A-Z" },
  { key: "name_desc", label: "Name Z-A" },
];

export default function ClientsOverview() {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);

  const [qtext, setQtext] = useState("");
  const [sortKey, setSortKey] = useState("created_desc");

  useEffect(() => {
    const q = query(collection(db, "clients"), orderBy("createdAt", "desc"), limit(500));

    const unsub = onSnapshot(
      q,
      (snap) => {
        setClients(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => {
        setClients([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const filteredSorted = useMemo(() => {
    const needle = qtext.trim().toLowerCase();

    let arr = clients.filter((c) => {
      if (!needle) return true;
      const name = String(c.name || "").toLowerCase();
      return name.includes(needle);
    });

    arr = [...arr].sort((a, b) => {
      const aCreated = getMillis(a.createdAt);
      const bCreated = getMillis(b.createdAt);
      const aUpdated = getMillis(a.lastUpdatedAt) || aCreated;
      const bUpdated = getMillis(b.lastUpdatedAt) || bCreated;
      const aName = String(a.name || "").toLowerCase();
      const bName = String(b.name || "").toLowerCase();

      switch (sortKey) {
        case "created_asc":
          return aCreated - bCreated;
        case "updated_desc":
          return bUpdated - aUpdated;
        case "name_asc":
          return aName.localeCompare(bName);
        case "name_desc":
          return bName.localeCompare(aName);
        case "created_desc":
        default:
          return bCreated - aCreated;
      }
    });

    return arr;
  }, [clients, qtext, sortKey]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div>
          <h2 style={{ marginBottom: 6 }}>Clients</h2>
          <p className="micro muted" style={{ margin: 0 }}>
            Showing {filteredSorted.length} of {clients.length}
          </p>
        </div>

        <div style={{ display: "grid", gap: 10, minWidth: 320 }}>
          <input
            type="text"
            placeholder="Search first or last name"
            value={qtext}
            onChange={(e) => setQtext(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.15)",
              background: "rgba(255,255,255,0.7)",
            }}
          />

          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 12,
              border: "1px solid rgba(0,0,0,0.15)",
              background: "rgba(255,255,255,0.7)",
            }}
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                Sort: {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <p className="muted">Loading...</p>}

      {!loading && filteredSorted.length === 0 && (
        <p className="muted" style={{ marginTop: 12 }}>
          No clients match your search.
        </p>
      )}

      {!loading && filteredSorted.length > 0 && (
        <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
          {filteredSorted.map((c) => (
            <article
              key={c.id}
              style={{
                border: "1px solid rgba(0,0,0,0.1)",
                borderRadius: 14,
                padding: 14,
                background: "rgba(255,255,255,0.7)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                }}
              >
                <div style={{ minWidth: 260 }}>
                  <strong style={{ display: "block" }}>{c.name || "Client"}</strong>
                  {c.phone && (
                    <div className="micro muted" style={{ marginTop: 4 }}>
                      {c.phone}
                    </div>
                  )}
                  <div className="micro muted" style={{ marginTop: 4 }}>
                    {formatAddress(c)}
                  </div>

                </div>

                <div style={{ textAlign: "right", minWidth: 220 }}>
                  <div
                    style={{
                      marginTop: 10,
                      display: "flex",
                      gap: 8,
                      justifyContent: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <Link className="btn" to={`/admin/clients/${c.id}`}>
                      View
                    </Link>
                    <Link className="btn primary" to={`/admin/clients/${c.id}/edit`}>
                      Edit
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
